import React, { useState, useEffect } from 'react';
import { formatDate, formatCurrency } from '../utils/payrollUtils';
import { supabase } from '../supabaseClient';

const PaystubPeriodList = ({ paystubs, selectedPaystub, onSelect, onLogout }) => {
  const [periodLabels, setPeriodLabels] = useState({});

  useEffect(() => {
    const fetchPeriodLabels = async () => {
      try {
        const { data: periods, error } = await supabase
          .from('payroll_period')
          .select('payroll_number, period_label, pay_date')
          .order('payroll_number', { ascending: false });

        if (error) {
          console.error('Error fetching period labels:', error);
          // Fallback to hardcoded labels
          setPeriodLabels({
            2513: { label: 'June 9-22, 2025', pay_date: '2025-06-27' },
            2514: { label: 'June 23 - July 6, 2025', pay_date: '2025-07-11' },
            2515: { label: 'July 7-20, 2025', pay_date: '2025-07-25' },
            2516: { label: 'July 21 - August 3, 2025', pay_date: '2025-08-08' }
          });
        } else {
          const labels = {};
          periods.forEach(period => {
            labels[period.payroll_number] = {
              label: period.period_label,
              pay_date: period.pay_date
            };
          });
          setPeriodLabels(labels);
        }
      } catch (error) {
        console.error('Error fetching period labels:', error);
      }
    };

    fetchPeriodLabels();
  }, []);

  // Group paystubs by payroll_number
  const groupedPaystubs = paystubs.reduce((groups, paystub) => {
    const payrollNumber = paystub.payroll_number;
    const periodInfo = periodLabels[payrollNumber];
    const displayLabel = periodInfo?.label || `Period ${payrollNumber}`;
    
    if (!groups[displayLabel]) {
      groups[displayLabel] = {
        paystubs: [],
        periodInfo: periodInfo
      };
    }
    groups[displayLabel].paystubs.push(paystub);
    return groups;
  }, {});

  // Calculate totals for each period
  const calculatePeriodTotals = (periodPaystubs) => {
    const totalGross = periodPaystubs.reduce((sum, paystub) => 
      sum + parseFloat(paystub.total_pay || 0), 0);
    
    const totalDeductions = periodPaystubs.reduce((sum, paystub) => 
      sum + parseFloat(paystub.employee_deductions || 0), 0);
    
    const totalTaxes = periodPaystubs.reduce((sum, paystub) => 
      sum + parseFloat(paystub.employee_taxes || 0), 0);
    
    const totalNet = totalGross - totalDeductions - totalTaxes;
    
    return { totalGross, totalDeductions, totalTaxes, totalNet };
  };

  return (
    <div className="bg-white rounded-none sm:rounded-lg shadow-none sm:shadow-lg p-2 sm:p-6 w-full max-w-6xl mx-auto paystub-period-card">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-3 sm:mb-6 gap-2 sm:gap-3">
        <h2 className="text-lg sm:text-2xl font-bold text-gray-800">Your Paystubs</h2>
        <button
          onClick={onLogout}
          className="px-3 py-2 text-sm sm:text-base bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors self-start sm:self-auto"
        >
          Logout
        </button>
      </div>
      
      <div className="space-y-4 sm:space-y-6">
        {Object.entries(groupedPaystubs).map(([period, groupData]) => {
          const periodPaystubs = groupData.paystubs;
          const totals = calculatePeriodTotals(periodPaystubs);
          const payDate = groupData.periodInfo?.pay_date || formatDate(new Date());
          
          return (
            <div key={period} className="border border-gray-300 rounded-none sm:rounded-lg overflow-hidden mx-0 sm:mx-0">
              {/* Period Summary Card */}
              <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-3 sm:p-4 border-b border-gray-300">
                <div className="flex flex-col gap-2 sm:flex-row sm:justify-between sm:items-center sm:gap-3">
                  <div className="flex-1">
                    <h3 className="text-base sm:text-xl font-bold text-gray-800">{period}</h3>
                    <p className="text-xs sm:text-base text-gray-600">Pay Date: {formatDate(payDate)}</p>
                    <p className="text-xs text-gray-500">
                      {periodPaystubs.length} paystub{periodPaystubs.length > 1 ? 's' : ''}
                    </p>
                  </div>
                  <div className="flex justify-between sm:block sm:text-right">
                    <div>
                      <span className="text-xs text-gray-500">Total Gross: </span>
                      <span className="font-semibold text-gray-800 text-sm">
                        {formatCurrency(totals.totalGross)}
                      </span>
                    </div>
                    <div>
                      <span className="text-xs text-gray-500">Total Net: </span>
                      <span className="text-sm sm:text-lg font-bold text-green-600">
                        {formatCurrency(totals.totalNet)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Individual Paystubs - Show each company separately */}
              <div className="divide-y divide-gray-200">
                {periodPaystubs.map((paystub, index) => {
                    const netPay = parseFloat(paystub.total_pay || 0) - 
                                 parseFloat(paystub.employee_deductions || 0) - 
                                 parseFloat(paystub.employee_taxes || 0);
                    
                    return (
                      <div
                        key={paystub.id || index}
                        onClick={() => onSelect(paystub)}
                        className={`p-3 sm:p-4 cursor-pointer transition-all hover:bg-gray-50 ${
                          selectedPaystub?.id === paystub.id
                            ? 'bg-blue-50 border-l-4 border-blue-500'
                            : ''
                        }`}
                      >
                        {/* Mobile Layout */}
                        <div className="block sm:hidden">
                          <div className="flex justify-between items-center mb-3">
                            <div className="flex-1">
                              <p className="text-base font-bold text-gray-900">
                                {paystub.company || 'N/A'}
                              </p>
                              <p className="text-xs text-gray-500">
                                Department: {paystub.department || 'N/A'}
                              </p>
                            </div>
                          </div>
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span className="text-xs text-gray-600">Gross Pay</span>
                              <span className="text-sm font-semibold text-gray-800">
                                {formatCurrency(paystub.total_pay)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-xs text-gray-600">Deductions</span>
                              <span className="text-sm font-medium text-red-600">
                                -{formatCurrency(
                                  parseFloat(paystub.employee_deductions || 0) + 
                                  parseFloat(paystub.employee_taxes || 0)
                                )}
                              </span>
                            </div>
                            <div className="flex justify-between items-center border-t pt-2">
                              <div>
                                <span className="text-sm font-medium text-gray-800">Net Pay</span>
                                <br />
                                <span className="text-base font-bold text-green-600">
                                  {formatCurrency(netPay)}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 text-gray-600">
                                <span className="text-sm">See details</span>
                                <svg 
                                  className="w-5 h-5 text-gray-400" 
                                  fill="none" 
                                  stroke="currentColor" 
                                  viewBox="0 0 24 24"
                                >
                                  <path 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round" 
                                    strokeWidth={2} 
                                    d="M9 5l7 7-7 7" 
                                  />
                                </svg>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Desktop Layout */}
                        <div className="hidden sm:flex justify-between items-center">
                          <div className="flex-1">
                            <div className="flex items-center space-x-4">
                              <div>
                                <p className="font-medium text-gray-900">
                                  {paystub.company || 'N/A'}
                                </p>
                                <p className="text-sm text-gray-500">
                                  Department: {paystub.department || 'N/A'}
                                </p>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex space-x-6 text-right">
                            <div>
                              <p className="text-xs text-gray-500">Gross Pay</p>
                              <p className="font-semibold text-gray-800">
                                {formatCurrency(paystub.total_pay)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Deductions</p>
                              <p className="font-medium text-red-600">
                                -{formatCurrency(
                                  parseFloat(paystub.employee_deductions || 0) + 
                                  parseFloat(paystub.employee_taxes || 0)
                                )}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-500">Net Pay</p>
                              <p className="text-lg font-bold text-green-600">
                                {formatCurrency(netPay)}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 text-gray-600">
                              <span className="text-sm">See details</span>
                              <svg 
                                className="w-5 h-5 text-gray-400" 
                                fill="none" 
                                stroke="currentColor" 
                                viewBox="0 0 24 24"
                              >
                                <path 
                                  strokeLinecap="round" 
                                  strokeLinejoin="round" 
                                  strokeWidth={2} 
                                  d="M9 5l7 7-7 7" 
                                />
                              </svg>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                }
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PaystubPeriodList;